import React, { memo, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import { Helmet } from 'react-helmet'

import {
	Experience,
	Skills,
	Values,
	Stories,
	Settings,
	CvFiles,
} from './components'
import { EntityAboutSection, EntityHeader, Loading, Read } from 'components'
import styles from './styles'
import { TProfileProps, EEntityAboutSectionType, EEntity } from 'models'
import { StoreValue } from 'store'
import { buildUserAvatar } from 'utils'
import { useUserCollectionQuery } from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const ProfileView: React.FunctionComponent<TProfileProps> = ({
	storeUser,
	storeUserPageViewAs,
}) => {
	const {
		personID,
		profileSection,
		articleSlug,
		postId,
	}: {
		personID?: string
		profileSection?: string
		articleSlug?: string
		postId?: string
	} = useParams()

	const history = useHistory()
	const classes = useStyles()
	const { t, i18n } = useTranslation()

	const tabs: { [key: string]: string } = {
		index: t('About me'),
		posts: t('My posts'),
	}

	const uid = personID || null
	// TODO: this function need to be replaced into a function that is executed when it is called.
	// TODO: WRONG: this query is a mistake. It is executed always, because always will be an uid.
	const { data, loading, refetch } = useUserCollectionQuery({
		skip: !uid,
		fetchPolicy: 'network-only',
		variables: {
			filter: {
				// id: { eq: uid },
				uid: { eq: uid },
			},
			// id: null,
			// uid,
		},
	})
	const userDetails =
		data && data?.userCollection?.edges?.map(x => x.node)?.[0]

	// const userobj = {
	// 	id: Math.random(),
	// 	firstName: userDetails.profileCollection?.edges[0].node?.firstName,
	// 	lastName: userDetails.profileCollection?.edges[0].node?.lastName,
	// 	user: '',
	// 	created_at: new Date().toISOString(),
	// 	updated_at: new Date().toISOString(),
	// }
	const currentTabIndex = useMemo(() => {
		return profileSection || 'index'
	}, [profileSection])

	let userName =
		(userDetails &&
			`${userDetails.profileCollection?.edges[0].node?.firstName} ${userDetails.profileCollection?.edges[0].node?.lastName}`) ??
		null

	const toggleTabIndex = useCallback(
		(index: 'index' | 'posts' | 'settings') => {
			let pathname = `/${i18n.language}/people/${uid}`
			if (index !== 'index')
				pathname = `/${i18n.language}/people/${uid}/${index}`

			history.push(pathname)
		},
		[history, i18n, uid],
	)
	const canEdit =
		(storeUser && storeUser.god) ||
		(userDetails && storeUser && storeUser?.uid === userDetails?.uid) ||
		false

	const bCanEdit = storeUserPageViewAs === 'admin' && canEdit
	const ogUserAvatar = userDetails
		? buildUserAvatar(
				userDetails.profileCollection?.edges[0].node?.id ?? '',
				userDetails.profileCollection?.edges[0].node.avatarPath ?? '',
		  )
		: '' // TODO: I need to add a placeholder

	if (loading && !userDetails) return <Loading />

	// COMMENTED BECAUSE its affect to load create company page //

	// else if (!data || !data.user) return <Redirect to={`/${i18n.language}`} />
	return (
		<>
			<Helmet>
				<meta property="og:title" content={userName ?? ''} />
				<meta property="og:url" content={window.location.href} />
				<meta property="og:image" content={ogUserAvatar} />
			</Helmet>
			{(articleSlug || postId) && <Read />}
			<Grid
				className={classes.profileContainer}
				container={true}
				direction="row"
			>
				<EntityHeader
					canEdit={canEdit}
					toggle={toggleTabIndex}
					currentTabIndex={currentTabIndex}
					name={userName}
					description={
						userDetails?.profileCollection?.edges?.[0]?.node
							?.experienceCollection?.edges?.[0]?.node
							?.position ?? ''
					}
					tabs={tabs}
					type={EEntity.person}
					user={userDetails}
				/>

				{currentTabIndex === 'index' && (
					<Grid
						className={classes.profileMainContent}
						container={true}
						direction="row"
					>
						<Skills
							skills={
								userDetails?.profileCollection?.edges?.[0]?.node?._ProfileToSkillCollection?.edges?.map(
									x => x.node,
								) as any
							}
							canEdit={bCanEdit}
							updateUser={refetch}
						/>
						<Values
							values={
								userDetails?.profileCollection?.edges[0]?.node?._ProfileToValueCollection?.edges?.map(
									edge => edge.node.value,
								) as any
							}
							canEdit={bCanEdit}
							updateUser={refetch}
						/>
						<EntityAboutSection
							type={EEntityAboutSectionType.profileAbout}
							title={t('About')}
							emphasizedTitle={t('me')}
							oneRowTitle={true}
							content={
								userDetails?.profileCollection?.edges[0].node
									.aboutMe
							}
							canEdit={bCanEdit}
							user={userDetails || undefined}
						/>

						{storeUser && storeUser?.uid === userDetails?.uid && (
							<CvFiles />
						)}
						<Experience
							canEdit={bCanEdit}
							items={
								userDetails?.profileCollection?.edges[0]?.node?.experienceCollection?.edges?.map(
									edge => edge.node,
								) as any
							}
							updateUser={refetch}
						/>
					</Grid>
				)}

				{currentTabIndex === 'posts' && (
					<Stories uid={personID} userName={userName} />
				)}
				{currentTabIndex === 'settings' && canEdit && (
					<Settings canEdit={canEdit} updateUser={refetch} />
				)}
			</Grid>
		</>
	)
}

export default memo((props: TProfileProps) => (
	<StoreValue
		keys={['user|storeUser', 'guides.userPageViewAs|storeUserPageViewAs']}
	>
		<ProfileView {...props} />
	</StoreValue>
))
